<template>
    <app-layout>
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">
                Manage Splits
            </h2>
        </template>

        <div>
            <div class="max-w-7xl mx-auto py-10 sm:px-6 lg:px-8">
                <div class="mb-4 text-sm shadow-sm sm:rounded-md flex items-stretch justify-start">
                    <div class="p-2 bg-glm-red text-red-100 flex items-center justify-center sm:rounded-l-md">
                        <font-awesome-icon
                            :icon="['fad', 'info']"
                            class="fa-fw"
                            size="lg"
                        ></font-awesome-icon>
                    </div>

                    <p class="p-2 bg-red-100 text-glm-red flex-1 sm:rounded-r-md">
                        Changes to the "value" field will destroy the graphs!<br><span class="font-bold">Please coordinate with the development team!</span>
                    </p>
                </div>

                <FormSection @submitted="submitCreateSplitForm">
                    <template #title>
                        Create a new split
                    </template>

                    <template #description>
                        Create a new split which can be used in the graphs.
                    </template>

                    <template #form>
                        <div class="col-span-6 sm:col-span-4">
                            <Label for="value" value="Value"/>
                            <Input id="value" type="text" class="mt-1 block w-full" v-model="createSplitForm.value"
                                   autocomplete="split-value" ref="createSplitValue"/>
                            <InputError :message="createSplitForm.errors.value" class="mt-2"/>
                            <HelpText>
                                Matches the second part of the filename e.g. pooled_<u>marital</u>_data.csv<br>
                                <span class="text-red-500">The file must contain a column named like this value.</span>
                            </HelpText>
                        </div>

                        <div class="col-span-6 sm:col-span-4">
                            <Label for="label" value="Label"/>
                            <Input id="label" type="text" class="mt-1 block w-full" v-model="createSplitForm.label"
                                   autocomplete="split-label" ref="createSplitLabel"/>
                            <InputError :message="createSplitForm.errors.label" class="mt-2"/>
                            <HelpText>
                                Label which is shown to the user in the dropdown.
                            </HelpText>
                        </div>
                    </template>

                    <template #actions>
                        <ActionMessage :on="createSplitForm.recentlySuccessful" class="mr-3">
                            Created.
                        </ActionMessage>

                        <ActionMessage :on="createSplitForm.processing" class="mr-3">
                            Creating...
                        </ActionMessage>

                        <Button :class="{ 'opacity-25': createSplitForm.processing }"
                                :disabled="createSplitForm.processing">
                            Create
                        </Button>
                    </template>
                </FormSection>

                <SectionBorder/>

                <FormSection class="mt-10 sm:mt-0" @submitted="createSplitOption">
                    <template #title>
                        Create a new split option
                    </template>

                    <template #description>
                        Each split has some options. These can be created here. In the graphs, each of these options is
                        displayed as a separate line.
                        The options are determined within the files using the column that has the same name as the
                        splits value field.
                    </template>

                    <template #form>
                        <div class="col-span-6 sm:col-span-4">
                            <Label for="value" value="Value"/>
                            <Input id="value" type="text" class="mt-1 block w-full"
                                   v-model="createSplitOptionForm.value"
                                   ref="splitOptionValue"
                                   autocomplete="split-option-value"/>
                            <InputError :message="createSplitOptionForm.errors.value" class="mt-2"/>
                            <HelpText>
                                From the file, from the column named as the splits value field, the value used to
                                distinguish the individual options.
                            </HelpText>
                        </div>

                        <div class="col-span-6 sm:col-span-4">
                            <Label for="label" value="Label"/>
                            <Input id="label" type="text" class="mt-1 block w-full"
                                   v-model="createSplitOptionForm.label"
                                   ref="splitOptionLabel"
                                   autocomplete="split-option-label"/>
                            <InputError :message="createSplitOptionForm.errors.label" class="mt-2"/>
                            <HelpText>
                                Label which is shown to the user in the graphs legend.
                            </HelpText>
                        </div>

                        <div class="col-span-6 sm:col-span-4">
                            <Label for="split" value="Split"/>
                            <Select class="mt-1 block w-full" v-model="createSplitOptionForm.split_id" :options="splits"
                                    optionKey="id" optionValue="value" defaultText="Select a split..."/>
                            <InputError :message="createSplitOptionForm.errors.split_id" class="mt-2"/>
                            <HelpText>
                                Split to which this option belongs to.
                            </HelpText>
                        </div>
                    </template>

                    <template #actions>
                        <ActionMessage :on="createSplitOptionForm.recentlySuccessful" class="mr-3">
                            Created.
                        </ActionMessage>

                        <ActionMessage :on="createSplitOptionForm.processing" class="mr-3">
                            Creating...
                        </ActionMessage>

                        <Button :class="{ 'opacity-25': createSplitOptionForm.processing }"
                                :disabled="createSplitOptionForm.processing">
                            Create
                        </Button>
                    </template>
                </FormSection>

                <template v-if="splits.length > 0">
                    <SectionBorder/>

                    <div class="flex items-center mb-6 mt-10 sm:mt-0 px-4 sm:px-0">
                        <h3 class="text-lg font-medium text-gray-900">Splits</h3>

                        <div class="ml-auto">
                            <Input
                                id="search"
                                type="text"
                                placeholder="Filter..."
                                class="block w-full"
                                v-model="splitSearch"
                            />
                        </div>
                    </div>

                    <table
                        class="w-full whitespace-nowrap sm:rounded-md bg-white divide-y divide-gray-300 overflow-hidden shadow">
                        <thead class="bg-gray-900">
                        <tr class="text-white">
                            <th class="font-semibold text-sm uppercase px-6 py-4 text-left sm:w-64">value</th>
                            <th class="font-semibold text-sm uppercase px-6 py-4 text-left hidden sm:table-cell">label</th>
                            <th class="font-semibold text-sm uppercase px-6 py-4 text-right w-1">&nbsp;</th>
                        </tr>
                        </thead>

                        <tbody class="divide-y divide-gray-200">
                        <tr v-for="(split, idx) in splitSearchResults" :key="idx"
                            :class="{'bg-gray-100' : idx % 2 !== 0}">
                            <td class="px-6 py-4 sm:w-64">
                                {{ split.value }}
                            </td>

                            <td class="px-6 py-4 hidden sm:table-cell">
                                {{ split.label }}

                                <template v-if="split.options.length > 0">
                                    <ul class="list-disc list-inside">
                                        <li v-for="(option, idx) in split.options" :key="idx">
                                            {{ option.label }}
                                        </li>
                                    </ul>
                                </template>
                                <template v-else>
                                    <br><span class="text-red-500"><i>None</i></span>
                                </template>
                            </td>

                            <td class="px-6 py-4 text-left w-1">
                                <DangerButton @click.native="confirmSplitDeletion(split)" title="Delete" class="mr-2">
                                    <font-awesome-icon
                                        :icon="['fad', 'trash']"
                                    ></font-awesome-icon>
                                </DangerButton>

                                <SecondaryButton @click.native="editSplitOptions(split)" title="Edit Options">
                                    <font-awesome-icon
                                        :icon="['fad', 'list']"
                                    ></font-awesome-icon>
                                </SecondaryButton>

                                <SecondaryButton @click.native="editSplit(split)" title="Edit">
                                    <font-awesome-icon
                                        :icon="['fad', 'pencil']"
                                    ></font-awesome-icon>
                                </SecondaryButton>
                            </td>
                        </tr>
                        </tbody>
                    </table>

                    <DialogModal :show="confirmingSplitDeletion" @close="closeSplitDeletionModal">
                        <template #title>
                            Delete Split
                        </template>

                        <template #content>
                            Are you sure you want to delete this split?
                            After that, this split can no longer be used within the application.
                        </template>

                        <template #footer>
                            <SecondaryButton @click.native="closeSplitDeletionModal">
                                Nevermind
                            </SecondaryButton>

                            <DangerButton
                                class="ml-2"
                                @click.native="deleteSplit"
                                :class="{ 'opacity-25': processing }"
                                :disabled="processing"
                            >
                                Delete Split
                            </DangerButton>
                        </template>
                    </DialogModal>

                    <FormModal :show="editingSplitModal" @submitted="submitEditSplitForm">
                        <template #title>
                            Edit Split
                        </template>

                        <template #content>
                            <div class="grid grid-cols-6 gap-6">
                                <div class="col-span-6">
                                    <Label for="value" value="Value"/>
                                    <Input id="value" type="text" class="mt-1 block w-full"
                                           v-model="editSplitForm.value"
                                           autocomplete="split-value" ref="editSplitValue"/>
                                    <InputError :message="editSplitForm.errors.value" class="mt-2"/>
                                    <HelpText>
                                        Matches the second part of the filename e.g. pooled_<u>marital</u>_data.csv<br>
                                        <span
                                            class="text-red-500">The file must contain a column named like this value.</span>
                                    </HelpText>
                                </div>

                                <div class="col-span-6">
                                    <Label for="label" value="Label"/>
                                    <Input id="label" type="text" class="mt-1 block w-full"
                                           v-model="editSplitForm.label"
                                           autocomplete="split-label" ref="editSplitLabel"/>
                                    <InputError :message="editSplitForm.errors.label" class="mt-2"/>
                                    <HelpText>
                                        Label which is shown to the user in the dropdown.
                                    </HelpText>
                                </div>

                                <div class="col-span-6">
                                    <Label for="order" value="Order"/>
                                    <Input id="order" type="text" class="mt-1 block w-full"
                                           v-model="editSplitForm.order"
                                           autocomplete="split-order" ref="editSplitOrder"/>
                                    <InputError :message="editSplitForm.errors.order" class="mt-2"/>
                                    <HelpText>
                                        On which position the split should be shown in the dropdown.
                                    </HelpText>
                                </div>
                            </div>
                        </template>

                        <template #footer>
                            <SecondaryButton @click.native="closeEditSplitModal">
                                Nevermind
                            </SecondaryButton>

                            <Button type="submit"
                                    :class="{ 'opacity-25': editSplitForm.processing }"
                                    :disabled="editSplitForm.processing"
                            >
                                Save
                            </Button>
                        </template>
                    </FormModal>

                    <DialogModal :show="editingSplitOptionsModal" @close="closeSplitOptionsEditModal">
                        <template #title v-if="selectedSplit">
                            Edit Options for Split "{{ selectedSplit.value }}"
                        </template>

                        <template #content v-if="selectedSplit">
                            <Label value="Existing Options"/>

                            <template v-if="selectedSplit.options && selectedSplit.options.length > 0">
                                <div class="mt-1 grid grid-cols-12 gap-6">
                                    <div class="col-span-2">
                                        <Label value="Value"/>
                                    </div>

                                    <div class="col-span-10">
                                        <Label value="Label"/>
                                    </div>
                                </div>

                                <ul class="block w-full">
                                    <li v-for="option in selectedSplit.options">
                                        <form @submit.prevent="saveSplitOptionEdit($event, option)" class="mt-2">
                                            <div class="grid grid-cols-12 gap-6">
                                                <div class="col-span-2">
                                                    <Input type="text" class="block w-full" name="value"
                                                           :value="option.value"/>
                                                    <InputError :message="updateSplitOptionForm.errors.value"
                                                                class="mt-2"/>
                                                </div>

                                                <div class="col-span-7">
                                                    <Input type="text" class="block w-full" name="label"
                                                           :value="option.label"/>
                                                    <InputError :message="updateSplitOptionForm.errors.label"
                                                                class="mt-2"/>
                                                </div>

                                                <div class="col-span-3 flex items-center align-center">
                                                    <Button
                                                        class="mr-4"
                                                        :class="{ 'opacity-25': updateSplitOptionForm.processing || processing }"
                                                        :disabled="updateSplitOptionForm.processing || processing"
                                                    >
                                                        Save
                                                    </Button>

                                                    <DangerButton
                                                        @click.native="confirmSplitOptionDeletion(option)"
                                                        :class="{ 'opacity-25': updateSplitOptionForm.processing || processing }"
                                                        :disabled="updateSplitOptionForm.processing || processing"
                                                    >
                                                        <font-awesome-icon :icon="['fad', 'trash']"></font-awesome-icon>
                                                    </DangerButton>
                                                </div>
                                            </div>
                                        </form>
                                    </li>
                                </ul>
                            </template>
                            <template v-else>
                                <span class="text-red-500"><i>None</i></span>
                            </template>

                            <div class="mt-6">
                                <Label value="New Option"/>

                                <form @submit.prevent="createSplitOptionForSplit" class="mt-1">
                                    <div class="mt-1 grid grid-cols-12 gap-6">
                                        <div class="col-span-2">
                                            <Label value="Value"/>
                                        </div>

                                        <div class="col-span-10">
                                            <Label value="Label"/>
                                        </div>
                                    </div>

                                    <div class="grid grid-cols-12 gap-6">
                                        <div class="col-span-2">
                                            <Input type="text" class="block w-full" name="value"
                                                   v-model="createSplitOptionForSplitForm.value"/>
                                            <InputError :message="createSplitOptionForSplitForm.errors.value"
                                                        class="mt-2"/>
                                        </div>

                                        <div class="col-span-8">
                                            <Input type="text" class="block w-full" name="label"
                                                   v-model="createSplitOptionForSplitForm.label"/>
                                            <InputError :message="createSplitOptionForSplitForm.errors.label"
                                                        class="mt-2"/>
                                        </div>

                                        <div class="col-span-2 flex items-center align-center">
                                            <Button
                                                class="mr-4"
                                                :class="{ 'opacity-25': createSplitOptionForSplitForm.processing }"
                                                :disabled="createSplitOptionForSplitForm.processing"
                                            >
                                                Save
                                            </Button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </template>

                        <template #footer>
                            <SecondaryButton @click.native="closeSplitOptionsEditModal">
                                Nevermind
                            </SecondaryButton>
                        </template>
                    </DialogModal>

                    <DialogModal :show="confirmingSplitOptionDeletion" @close="closeSplitOptionDeletionModal">
                        <template #title>
                            Delete Split Option
                        </template>

                        <template #content>
                            Are you sure you want to delete this split option?
                            After that, this split can no longer be used within the application.
                        </template>

                        <template #footer>
                            <SecondaryButton @click.native="closeSplitDeletionModal">
                                Nevermind
                            </SecondaryButton>

                            <DangerButton
                                class="ml-2"
                                @click.native="deleteSplitOption"
                                :class="{ 'opacity-25': processing }"
                                :disabled="processing"
                            >
                                Delete Split Option
                            </DangerButton>
                        </template>
                    </DialogModal>
                </template>
            </div>
        </div>
    </app-layout>
</template>

<script>
import AppLayout from '@/Layouts/AppLayout';
import FormSection from "@/Jetstream/FormSection";
import Label from "@/Jetstream/Label";
import Input from "@/Jetstream/Input";
import InputError from "@/Jetstream/InputError";
import HelpText from "@/Jetstream/HelpText";
import ActionMessage from "@/Jetstream/ActionMessage";
import Button from "@/Jetstream/Button";
import SectionBorder from "@/Jetstream/SectionBorder";
import DangerButton from "@/Jetstream/DangerButton";
import SecondaryButton from "@/Jetstream/SecondaryButton";
import DialogModal from "@/Jetstream/DialogModal";
import FormModal from "@/Jetstream/FormModal";
import Select from "@/Jetstream/Select";

export default {
    components: {
        Select,
        FormModal,
        DialogModal,
        SecondaryButton,
        DangerButton,
        SectionBorder,
        Button,
        ActionMessage,
        HelpText,
        InputError,
        Input,
        Label,
        FormSection,
        AppLayout,
    },

    props: ['splits'],

    data() {
        return {
            splitSearch: null,

            confirmingSplitDeletion: false,
            confirmingSplitOptionDeletion: false,
            processingDeletion: false,
            selectedSplit: null,
            seletedSplitOption: null,

            editingSplitModal: false,
            editingSplitOptionsModal: false,

            processing: false,

            createSplitForm: this.$inertia.form({
                value: '',
                label: '',
            }),

            editSplitForm: this.$inertia.form({
                value: '',
                label: '',
                order: '',
            }),

            createSplitOptionForm: this.$inertia.form({
                value: '',
                label: '',
                split_id: '',
            }),

            createSplitOptionForSplitForm: this.$inertia.form({
                value: '',
                label: '',
                split_id: '',
            }),

            updateSplitOptionForm: this.$inertia.form({
                value: '',
                label: '',
            }),
        };
    },

    computed: {
        splitSearchResults() {
            if (this.splitSearch === null) {
                return this.splits;
            }

            return this.splits.filter(e => e.value.includes(this.splitSearch) || e.label.includes(this.splitSearch));
        },
    },

    methods: {
        confirmSplitOptionDeletion(option) {
            this.seletedSplitOption = option
            this.confirmingSplitOptionDeletion = true;
        },

        closeSplitOptionDeletionModal() {
            this.confirmingSplitOptionDeletion = false;
            this.seletedSplitOption = null
        },

        deleteSplitOption() {
            this.processing = true

            this.$inertia.delete(route('intern.split.option.destroy', this.seletedSplitOption.id), {
                preserveScroll: true,
                onSuccess: () => {
                    this.processing = false

                    this.closeSplitOptionDeletionModal()
                    this.closeSplitOptionsEditModal()
                },
                onError: () => {
                    this.processing = false
                }
            })
        },

        closeSplitOptionsEditModal() {
            this.editingSplitOptionsModal = false;
            this.selectedSplit = null;

            this.createSplitOptionForSplitForm.reset();
            this.createSplitOptionForSplitForm.clearErrors();
        },

        createSplitOptionForSplit() {
            this.createSplitOptionForSplitForm.split_id = this.selectedSplit.id

            this.createSplitOptionForSplitForm.put(route('intern.split.option.create'), {
                errorBag: 'createSplitOption',
                preserveScroll: true,
                onSuccess: () => {
                    this.closeSplitOptionsEditModal()
                },
            });
        },

        editSplitOptions(split) {
            this.selectedSplit = split;
            this.editingSplitOptionsModal = true;
        },

        saveSplitOptionEdit(e, splitOption) {
            this.updateSplitOptionForm.value = e.target.elements.value.value
            this.updateSplitOptionForm.label = e.target.elements.label.value

            this.updateSplitOptionForm.put(route('intern.split.option.update', splitOption.id), {
                errorBag: 'updateSplitOption',
                preserveScroll: true,
                onSuccess: () => {
                    this.updateSplitOptionForm.reset()
                },
            })
        },

        createSplitOption() {
            this.createSplitOptionForm.put(route('intern.split.option.create'), {
                errorBag: 'createSplitOption',
                preserveScroll: true,
                onSuccess: () => {
                    this.createSplitOptionForm.reset();
                },
            });
        },

        submitEditSplitForm() {
            this.editSplitForm.post(route('intern.split.update', this.selectedSplit.id), {
                errorBag: 'updateSplit',
                preserveScroll: true,
                onSuccess: () => {
                    this.closeEditSplitModal()
                },
            })
        },

        confirmSplitDeletion(split) {
            this.confirmingSplitDeletion = true;
            this.selectedSplit = split
        },

        editSplit(split) {
            this.selectedSplit = split

            this.editSplitForm.value = split.value
            this.editSplitForm.label = split.label
            this.editSplitForm.order = split.order

            this.editingSplitModal = true
        },

        deleteSplit() {
            this.processing = true

            this.$inertia.delete(route('intern.split.destroy', this.selectedSplit.id), {
                onSuccess: () => {
                    this.processing = false

                    this.closeSplitDeletionModal()
                },
                onError: () => {
                    this.processing = false
                }
            })
        },

        closeSplitDeletionModal() {
            this.confirmingSplitDeletion = false;
            this.selectedSplit = null
        },

        closeEditSplitModal() {
            this.editingSplitModal = false

            this.selectedSplit = null
            this.editSplitForm.reset()
        },

        submitCreateSplitForm() {
            this.createSplitForm.put(route('intern.split.create'), {
                errorBag: 'createSplit',
                preserveScroll: true,
                onSuccess: () => {
                    this.createSplitForm.reset();
                },
            });
        }
    }
}
</script>
